import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { ChangeDetectionStrategy, Component, DestroyRef, Injector, effect, inject, input, output, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes, dndStitchPredicate, dragDataTypeAllowed, type DragData } from '@modules/drag-n-drop/types/drag-data';
import { LinkedInfo } from '@modules/linked-info/types/linked-info';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { QuickActionButtonComponent } from '@modules/elements/components/quick-forms/quick-action-button/quick-action-button.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { type Stitch } from '@modules/common/types/stitch';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { Sheet } from '@modules/sheets/types/sheet';

/**
 * SBUX-1043 — the fourth panel's "+ New workbook", as **Slides' panel does it**.
 *
 * Oleg, 2026-10-10: *"The drag and drop behavior should follow how Slides works now. And yes, as
 * always in our app, a drag and drop always must create a stitch — that is a keystone behavior and
 * rule."* So this is `slides/components/panel-deck-create/` in Sheets' terms, and not the shared
 * `QuickFormComponent` the panel used before, whose `handleDrop` only pre-fills a form and stitches
 * nothing.
 *
 * Two behaviours come from that:
 *
 * - **The click opens a menu, and Blank creates outright.** `drag-and-drop.md` §4.5 says a
 *   quick-create bar's click "opens the form just as a drop does"; Slides' panel already does not,
 *   and the Design Lab does not either. "Create with Stitchy" is in Slides' menu and is deliberately
 *   absent here — Oleg, same message: *"You can drop Create with Stitchy for now."*
 * - **A drop creates the workbook and then stitches it to what was dropped.** The link is a real
 *   `POST /api/stitched-items` through `LinkedInfoService.linkItems`, owning item first.
 *
 * **It looks like the Design Lab's** (2026-10-12): the row is the create trigger and its hint —
 * "Creating…" while busy, else "Drag to create" ("Drop to create" while over it) — and nothing else.
 * Every failure is a toast, as the prototype's drop handler reports them, worded by what failed:
 *
 * - a dropped thing that is not a Stitch item: the prototype's "Drag an email, event, …" line;
 * - the create: "This workbook couldn't be created." and nothing is left pending;
 * - the link, after the workbook was made: "The workbook was created, but its source link was not
 *   saved." with **Retry**, which finishes that link without creating again and then the rest of the
 *   batch. Closing the toast without Retry forgets the rest of the batch.
 *
 * Nothing else is said: an HTTP failure is not an `Error`, and the catch-all that mapped every
 * non-`Error` to "Could not resolve the source link." said that about a failed create or link too.
 */
@Component({
  selector: 'app-panel-sheet-create',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './panel-sheet-create.component.html',
  imports: [QuickActionButtonComponent, DragNDropModule, ContextMenuItemComponent, ContextMenuWindowComponent, PopoverDirective],
})
export class PanelSheetCreateComponent {
  /** The folder being browsed, so a workbook made here is filed where the user is looking. */
  public readonly sheetFolderId = input<string | null>(null);

  /** The panel's leave guard: a workbook open over the list is asked before anything is created. */
  public readonly beforeCreate = input<() => Promise<boolean>>(async () => true);

  /** A workbook this control created. The panel opens it; this never navigates. */
  public readonly created = output<Sheet>();

  /** The two menu entries the panel already owns a flow for. */
  public readonly templateRequested = output<void>();
  public readonly importRequested = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly sheetsService = inject(SheetsService);
  private readonly context = inject(SpaceContextStore);
  private readonly toaster = inject(ToasterService);

  protected readonly S = SHEETS_STRINGS;
  protected readonly dragOver = signal(false);
  protected readonly busy = signal(false);

  /** The rest of a dropped batch, and the link that has not been written yet. */
  private pendingDrop: { data: DragData; next: number; folder: string | null; space: string | null | undefined } | undefined;
  private pendingLink: { items: LinkedInfo[]; made: Sheet; space: string | null | undefined } | undefined;
  /** The open "…source link was not saved." toast, whose Retry finishes `pendingLink`. */
  private linkToast: ReturnType<ToasterService['show']> | undefined;

  /**
   * A workbook can be dropped on this too — it makes a second workbook stitched to the first, which
   * is what Slides does with a deck. Everything else must be stitchable: a tag or a colour is not an
   * item, so there would be nothing to link to. While a batch is unfinished (its Retry toast is up),
   * another drop waits for it.
   */
  protected readonly accepts = (data: DragData): boolean =>
    !this.busy() && !this.pendingDrop && !!data?.data.length && dragDataTypeAllowed(data.type) && dndStitchPredicate(data.type);

  public constructor() {
    // A space change invalidates everything half-written: the items belong to the space they were
    // dropped in, and retrying them against another one would file them in the wrong place.
    effect(() => {
      this.context.contextId();
      untracked(() => this.forget());
    });
    // A Retry offered by a control that is gone would do nothing.
    this.destroyRef.onDestroy(() => this.forget());
  }

  /** Drops the unfinished batch, and the toast offering to finish it. */
  private forget(): void {
    this.pendingDrop = undefined;
    this.pendingLink = undefined;
    const toast = this.linkToast;
    this.linkToast = undefined;
    toast?.toastRef?.close();
  }

  protected blank(): void {
    void this.guarded(async () => {
      const made = await firstValueFrom(this.sheetsService.create(this.draft(this.S['Untitled workbook']), { emit: true, toast: true }));
      this.created.emit(made);
    });
  }

  protected chooseTemplate(): void {
    void this.guarded(async () => this.templateRequested.emit());
  }

  protected chooseImport(): void {
    void this.guarded(async () => this.importRequested.emit());
  }

  /** Everything this control starts asks the panel's open workbook first, as its own doors do. */
  private async guarded(run: () => Promise<void>): Promise<void> {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    if (this.busy()) return;
    if (!(await this.beforeCreate()())) return;
    this.busy.set(true);
    try {
      await run();
    } catch {
      // Only Blank's create can fail here; the other two hand over to the panel.
      this.toaster.show({ text: this.S["This workbook couldn't be created."] });
    } finally {
      this.busy.set(false);
    }
  }

  private draft(title: string): Sheet {
    return new Sheet({ title: title.trim().slice(0, 200) || this.S['Untitled workbook'], sheetFolderId: this.sheetFolderId() ?? null });
  }

  protected async drop(data: DragData): Promise<void> {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.dragOver.set(false);
    if (!this.accepts(data)) return;
    if (!(await this.beforeCreate()())) return;
    // A copy: the drag's own array is the source list's and goes on living after the drop.
    this.pendingDrop = { data: { ...data, data: [...data.data] }, next: 0, folder: this.sheetFolderId() ?? null, space: this.context.contextId() };
    await this.resume();
  }

  /** The link toast's Retry: the link that failed, then the rest of the batch. */
  protected async retryLink(): Promise<void> {
    await this.resume();
  }

  /**
   * One item at a time, so a batch that fails halfway leaves the items before it created AND
   * stitched, and resumes at the one that failed rather than making duplicates.
   */
  private async resume(): Promise<void> {
    const batch = this.pendingDrop;
    if (!batch || this.busy() || this.destroyRef.destroyed || batch.space !== this.context.contextId()) return;
    this.busy.set(true);
    try {
      while (batch.next < batch.data.data.length) {
        if (!this.current(batch)) return;
        // A link left over from the last attempt is finished before anything else is created.
        if (this.pendingLink) {
          // Held until the link lands: `created` opens the workbook over the list, and the list is
          // where THIS control lives — so emitting before the stitch destroys the component holding
          // `pendingLink`, and its Retry with it. The user would be left with a correctly named
          // workbook and no stitch. Slides' panel emits after `finishPendingCreation` for the same reason.
          const made = this.pendingLink.made;
          const link = await this.finishLink();
          if (link === 'failed') this.offerRetry(batch);
          if (link !== 'linked') return;
          batch.next++;
          this.created.emit(made);
          continue;
        }
        const source = batch.data.data[batch.next] as Partial<Stitch> & { title?: string };
        if (!source?.id || !source.itemType) {
          // Nothing to stitch to, so nothing is created for it, nor for what follows it.
          this.forget();
          this.toaster.show({ text: this.S['Drag an email, event, task, note, slide, file, contact or workbook from Stitch.'] });
          return;
        }
        let made: Sheet;
        try {
          made = await firstValueFrom(this.sheetsService.create(this.draft(source.title ?? ''), { emit: true, toast: false }));
        } catch {
          // Whatever was thrown — an `HttpErrorResponse` is not an `Error` — the create is what failed.
          if (this.pendingDrop === batch) this.forget();
          this.toaster.show({ text: this.S["This workbook couldn't be created."] });
          return;
        }
        if (!this.current(batch)) return;
        // Owning item first, as every caller of `linkItems` passes it.
        this.pendingLink = {
          items: [new LinkedInfo(made), new LinkedInfo({ id: source.id, itemType: source.itemType })],
          made,
          space: batch.space,
        };
      }
      if (this.pendingDrop === batch) this.forget();
    } finally {
      this.busy.set(false);
    }
  }

  private current(batch: NonNullable<typeof this.pendingDrop>): boolean {
    return !this.destroyRef.destroyed && batch.space === this.context.contextId() && this.pendingDrop === batch;
  }

  /**
   * The link half. `failed` whether the request errored or answered no; `stale` when the batch was
   * abandoned meanwhile. The link is kept on failure so the workbook never sits there unstitched and
   * unnoticed.
   */
  private async finishLink(): Promise<'linked' | 'failed' | 'stale'> {
    const pending = this.pendingLink;
    if (!pending || this.destroyRef.destroyed || pending.space !== this.context.contextId()) return 'stale';
    let linked = false;
    try {
      linked = await firstValueFrom(this.injector.get(LinkedInfoService).linkItems(pending.items));
    } catch {
      linked = false;
    }
    if (this.pendingLink !== pending || this.destroyRef.destroyed) return 'stale';
    if (!linked) return 'failed';
    this.pendingLink = undefined;
    return 'linked';
  }

  /** The link failed: say so, with Retry. Closing the toast without it forgets the rest of the batch. */
  private offerRetry(batch: NonNullable<typeof this.pendingDrop>): void {
    const toast = this.toaster.show({
      text: this.S['The workbook was created, but its source link was not saved.'],
      actions: [
        {
          text: this.S.Retry,
          handler: () => {
            if (this.linkToast === toast) this.linkToast = undefined;
            void this.retryLink();
          },
        },
      ],
      // Fires when the toast goes without its action having run: timed out, or closed.
      afterTimeout: () => {
        if (this.linkToast === toast) this.linkToast = undefined;
        if (this.pendingDrop === batch) this.forget();
      },
    });
    this.linkToast = toast;
  }

  protected readonly DragDataTypes = DragDataTypes;
}
